import { lensReaderStorageKey } from './lensReaderStorage';

export const PINNED_LENS_STORAGE_KEY = 'askrichard.pinned-lens-ids';
/** Display slots on the rail, not a limit on saved pins. */
export const MAX_PINNED_LENSES = 5;
export const LENS_PINS_CHANGED_EVENT = 'askrichard:lens-pins-changed';

export function clearLegacyLensPins(email?: string | null): void {
  const key = lensReaderStorageKey(PINNED_LENS_STORAGE_KEY, email);
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // The server remains authoritative when browser storage is unavailable.
  }
}

/** Reorder the whole saved list, including pins outside the rail's display slots. */
export function reorderPinnedIds(ids: Array<string>, active: string, over: string): Array<string> {
  const from = ids.indexOf(active);
  const to = ids.indexOf(over);
  if (from < 0 || to < 0 || from === to) return ids;
  const result = [...ids];
  result.splice(to, 0, ...result.splice(from, 1));
  return result;
}

export function readPinnedLensIds(email?: string | null): Array<string> {
  const key = lensReaderStorageKey(PINNED_LENS_STORAGE_KEY, email);
  if (typeof window === 'undefined' || !key) {
    return [];
  }

  try {
    const value = window.localStorage.getItem(key);
    const parsed: unknown = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function savePinnedLensIds(ids: Array<string>, email?: string | null): void {
  const key = lensReaderStorageKey(PINNED_LENS_STORAGE_KEY, email);
  if (typeof window === 'undefined' || !key) {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
    window.dispatchEvent(new Event(LENS_PINS_CHANGED_EVENT));
  } catch {
    // A pin is a local convenience. Private browsing or a full storage quota
    // must not prevent the workspace from rendering or a conversation opening.
  }
}

/** Keep every dashboard surface in sync with pin changes in this tab and other tabs. */
export function subscribeLensPins(listener: () => void, email?: string | null): () => void {
  const key = lensReaderStorageKey(PINNED_LENS_STORAGE_KEY, email);
  if (typeof window === 'undefined' || !key) {
    return () => {};
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === key) {
      listener();
    }
  };
  window.addEventListener(LENS_PINS_CHANGED_EVENT, listener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(LENS_PINS_CHANGED_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
}
